import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Trash2, FileSignature, Send, UserCheck, BadgeCheck, Paperclip,
  SlidersHorizontal, MessageSquare, FolderOpen, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { loanProfilingApi } from '@/services/api';
import { inr } from './CockpitUI';
import { Field, Picker, StatusPill, OfferUpload, DocLink, OfferCharges } from './OfferPieces';
import { OfferNegotiation } from './OfferNegotiation';

const CHANNELS = [
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'IN_PERSON', label: 'In person' },
];

// The label as the desk picked it — "over whatsapp" read like a typo.
const channelLabel = (value) =>
  CHANNELS.find((c) => c.value === value)?.label || (value || '').replace(/_/g, ' ');

const VARIANTS = [
  { value: 'WITH_BENEFITS', label: 'With Wisedrive benefits' },
  { value: 'WITHOUT_BENEFITS', label: 'Without benefits' },
];

const variantLabel = (value) => {
  // shares logged before the two copies existed cannot claim to be either
  if (!value) return 'copy not recorded';
  return (VARIANTS.find((v) => v.value === value) || VARIANTS[0]).label.toLowerCase();
};

const Metric = ({ label, value, testId, accent }) => (
  <div data-testid={testId}>
    <div className="text-[11px] text-slate-400">{label}</div>
    <div className={`text-sm font-semibold tabular-nums ${accent ? 'text-blue-700' : 'text-slate-800'}`}>{value}</div>
  </div>
);

const Tag = ({ children, tone = 'slate', testId }) => (
  <span data-testid={testId} className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded
    ${tone === 'emerald' ? 'bg-emerald-50 text-emerald-700' : tone === 'amber' ? 'bg-amber-50 text-amber-700' : tone === 'rose' ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-500'}`}>
    {children}
  </span>
);

const TermsEditor = ({ leadId, offer, onSaved, readOnly }) => {
  const snapshot = () => ({
    roi: offer.roi ?? '', tenure_months: offer.tenure_months ?? '',
    commission_pct: offer.commission_pct ?? '', loan_amount: offer.loan_amount ?? '',
    emi_date: offer.emi_date ?? '', bank_poc: offer.bank_poc ?? '',
  });
  const [f, setF] = useState(snapshot);
  const [saving, setSaving] = useState(false);

  // a negotiation round logged on the next tab moves the live terms — the editor
  // must follow, or saving it would quietly undo what the bank agreed
  useEffect(() => { setF(snapshot()); /* eslint-disable-next-line */ }, [offer.updated_at]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await loanProfilingApi.updateOffer(leadId, offer.id, {
        roi: f.roi === '' ? null : parseFloat(f.roi),
        tenure_months: f.tenure_months === '' ? null : parseInt(f.tenure_months, 10),
        commission_pct: f.commission_pct === '' ? null : parseFloat(f.commission_pct),
        loan_amount: f.loan_amount === '' ? null : parseFloat(f.loan_amount),
        emi_date: f.emi_date || null,
        bank_poc: f.bank_poc || null,
      });
      onSaved(res.data);
      toast.success('Terms updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the terms');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid={`offer-terms-${offer.id}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-800">Live terms</h4>
        {!readOnly && (
          <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid={`terms-save-${offer.id}`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}Save terms
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="Rate (%)">
          <Input type="number" step="0.05" value={f.roi} disabled={readOnly} onChange={(e) => setF((p) => ({ ...p, roi: e.target.value }))}
            className="mt-1 h-9" data-testid={`terms-roi-${offer.id}`} />
        </Field>
        <Field label="Tenure (months)">
          <Input type="number" value={f.tenure_months} disabled={readOnly} onChange={(e) => setF((p) => ({ ...p, tenure_months: e.target.value }))}
            className="mt-1 h-9" data-testid={`terms-tenure-${offer.id}`} />
        </Field>
        <Field label="Loan amount (₹)">
          <Input type="number" value={f.loan_amount} disabled={readOnly} onChange={(e) => setF((p) => ({ ...p, loan_amount: e.target.value }))}
            className="mt-1 h-9" data-testid={`terms-amount-${offer.id}`} />
        </Field>
        <Field label="Payout to us (%)" hint="Internal — never on the letter">
          <Input type="number" step="0.05" value={f.commission_pct} disabled={readOnly} onChange={(e) => setF((p) => ({ ...p, commission_pct: e.target.value }))}
            className="mt-1 h-9" data-testid={`terms-commission-${offer.id}`} />
        </Field>
        <Field label="EMI date">
          <Input value={f.emi_date} disabled={readOnly} onChange={(e) => setF((p) => ({ ...p, emi_date: e.target.value }))}
            className="mt-1 h-9" placeholder="5th of every month" data-testid={`terms-emidate-${offer.id}`} />
        </Field>
        <Field label="Bank contact">
          <Input value={f.bank_poc} disabled={readOnly} onChange={(e) => setF((p) => ({ ...p, bank_poc: e.target.value }))}
            className="mt-1 h-9" placeholder="Shashi, Kotak" data-testid={`terms-poc-${offer.id}`} />
        </Field>
      </div>
    </div>
  );
};

const LetterSection = ({ leadId, offer, onSaved, readOnly }) => {
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [share, setShare] = useState({ open: false, channel: 'WHATSAPP', to: '', note: '', variant: 'WITH_BENEFITS' });
  const letters = offer.letters || [];
  const latest = letters[letters.length - 1];

  const generate = async () => {
    setBusy(true);
    try {
      const res = await loanProfilingApi.generateOfferLetter(leadId, offer.id);
      onSaved(res.data);
      toast.success(`Offer letter v${res.data.letter?.version} ready — with and without benefits`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not build the letter');
    } finally { setBusy(false); }
  };

  const markSent = async () => {
    setBusy(true);
    try {
      const res = await loanProfilingApi.shareOffer(leadId, offer.id, {
        channel: share.channel, to: share.to || null, note: share.note || null, variant: share.variant,
      });
      onSaved(res.data);
      setShare({ open: false, channel: 'WHATSAPP', to: '', note: '', variant: 'WITH_BENEFITS' });
      toast.success('Logged as sent to the customer');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not log that');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`offer-letter-${offer.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-800">Offer letter</h4>
          <p className="text-[11px] text-slate-400">
            Each version is made twice — with the Wisedrive benefits and a clean copy for the bank or dealer.
          </p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={generate} disabled={busy} data-testid={`letter-generate-${offer.id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSignature className="w-3.5 h-3.5 mr-1.5" />}
              {letters.length ? 'Regenerate' : 'Generate letter'}
            </Button>
            {latest && (
              <Button size="sm" onClick={() => setShare((p) => ({ ...p, open: !p.open }))} data-testid={`letter-share-toggle-${offer.id}`}>
                <Send className="w-3.5 h-3.5 mr-1.5" />Mark as sent
              </Button>
            )}
          </div>
        )}
      </div>

      {share.open && (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 mb-3 grid grid-cols-2 gap-3" data-testid={`letter-share-form-${offer.id}`}>
          <Field label="Which copy">
            <Picker value={share.variant} onChange={(v) => setShare((p) => ({ ...p, variant: v }))} options={VARIANTS}
              testId={`share-variant-${offer.id}`} />
          </Field>
          <Field label="Sent over">
            <Picker value={share.channel} onChange={(v) => setShare((p) => ({ ...p, channel: v }))} options={CHANNELS}
              testId={`share-channel-${offer.id}`} />
          </Field>
          <Field label="Sent to">
            <Input value={share.to} onChange={(e) => setShare((p) => ({ ...p, to: e.target.value }))}
              className="mt-1 h-9" placeholder="Phone or email" data-testid={`share-to-${offer.id}`} />
          </Field>
          <Field label="Note">
            <Input value={share.note} onChange={(e) => setShare((p) => ({ ...p, note: e.target.value }))}
              className="mt-1 h-9" placeholder="Sent v2 after dropping the handling fee" data-testid={`share-note-${offer.id}`} />
          </Field>
          <div className="col-span-2 flex items-center justify-between">
            <p className="text-[11px] text-slate-400">Automatic WhatsApp and email delivery comes in the next release.</p>
            <Button size="sm" onClick={markSent} disabled={busy} data-testid={`share-save-${offer.id}`}>Log it</Button>
          </div>
        </div>
      )}

      {letters.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid={`letter-empty-${offer.id}`}>
          No letter yet. Add the rate, tenure and loan amount, then generate one.
        </p>
      ) : (
        <ul className="space-y-1.5" data-testid={`letter-versions-${offer.id}`}>
          {[...letters].reverse().slice(0, showAll ? letters.length : 3).map((l) => (
            <li key={l.version} className="flex flex-wrap items-center gap-3 text-xs text-slate-500" data-testid={`letter-version-${offer.id}-${l.version}`}>
              <span className="font-semibold text-slate-700">v{l.version}</span>
              <span className="tabular-nums">{l.snapshot?.roi}% · {l.snapshot?.tenure_months}m · EMI {inr(l.snapshot?.emi)}</span>
              <span className="text-slate-400">{l.letter_no}</span>
              <DocLink leadId={leadId} docId={l.doc_id_with_benefits || l.doc_id}
                name={l.benefits_count ? `With ${l.benefits_count} benefits` : 'With benefits'}
                testId={`letter-download-${offer.id}-${l.version}`} />
              {l.doc_id_plain && (
                <DocLink leadId={leadId} docId={l.doc_id_plain} name="Without benefits"
                  testId={`letter-download-plain-${offer.id}-${l.version}`} />
              )}
              <span className="text-slate-400 ml-auto">
                {new Date(l.generated_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} · {l.generated_by}
              </span>
            </li>
          ))}
          {letters.length > 3 && (
            <li>
              <button onClick={() => setShowAll((v) => !v)} className="text-xs text-blue-600 hover:underline"
                data-testid={`letter-versions-toggle-${offer.id}`}>
                {showAll ? 'Show fewer' : `Show ${letters.length - 3} older version${letters.length - 3 > 1 ? 's' : ''}`}
              </button>
            </li>
          )}
        </ul>
      )}

      {(offer.shares || []).length > 0 && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-100" data-testid={`offer-shares-${offer.id}`}>
          {offer.shares.map((s) => (
            <p key={s.id} className="text-[11px] text-slate-500">
              v{s.letter_version} <strong className="font-semibold text-slate-600">{variantLabel(s.variant)}</strong> sent
              over {channelLabel(s.channel)} {s.to ? `to ${s.to}` : ''} on{' '}
              {new Date(s.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              {s.note ? ` — ${s.note}` : ''}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

const DecisionSection = ({ leadId, offer, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [channel, setChannel] = useState('WHATSAPP');
  const decided = offer.customer_decision;
  const confirmed = offer.bank_confirmation;

  const act = async (fn, success) => {
    setBusy(true);
    try {
      const res = await fn();
      onSaved(res.data);
      setNote('');
      toast.success(success);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not record that');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`offer-decision-${offer.id}`}>
      <h4 className="text-sm font-semibold text-slate-800 mb-2">Customer's decision</h4>
      {decided ? (
        <p className="text-xs text-emerald-700 inline-flex items-center gap-1.5" data-testid={`decision-recorded-${offer.id}`}>
          <UserCheck className="w-3.5 h-3.5" />
          Customer chose this bank over {channelLabel(decided.channel)} on{' '}
          {new Date(decided.accepted_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })},
          recorded by {decided.recorded_by}{decided.note ? ` — ${decided.note}` : ''}
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <Field label="They told us over">
            <Picker value={channel} onChange={setChannel} options={CHANNELS} testId={`decision-channel-${offer.id}`} />
          </Field>
          <Field label="Note" className="flex-1 min-w-[200px]">
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 h-9"
              placeholder="Customer confirmed on the call" data-testid={`decision-note-${offer.id}`} />
          </Field>
          <Button size="sm" disabled={busy} data-testid={`decision-accept-${offer.id}`}
            onClick={() => act(() => loanProfilingApi.recordCustomerDecision(leadId, offer.id, { channel, note: note || null }),
              'Recorded the customer\'s choice')}>
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5 mr-1.5" />}
            Customer chose this bank
          </Button>
        </div>
      )}

      {decided && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          {confirmed ? (
            <p className="text-xs text-violet-700 inline-flex items-center gap-1.5" data-testid={`bank-confirmed-${offer.id}`}>
              <BadgeCheck className="w-3.5 h-3.5" />
              Confirmed to the bank over {channelLabel(confirmed.channel)} by {confirmed.confirmed_by} on{' '}
              {new Date(confirmed.confirmed_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              {confirmed.note ? ` — ${confirmed.note}` : ''}
            </p>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Confirmed to the bank over">
                <Picker value={channel} onChange={setChannel} options={CHANNELS} testId={`confirm-channel-${offer.id}`} />
              </Field>
              <Field label="Note" className="flex-1 min-w-[200px]">
                <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 h-9"
                  placeholder="Told Shashi to proceed" data-testid={`confirm-note-${offer.id}`} />
              </Field>
              <Button size="sm" variant="outline" disabled={busy} data-testid={`bank-confirm-${offer.id}`}
                onClick={() => act(() => loanProfilingApi.recordBankConfirmation(leadId, offer.id, { channel, note: note || null }),
                  'Confirmation to the bank recorded')}>
                <BadgeCheck className="w-3.5 h-3.5 mr-1.5" />Confirm to the bank
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ProofSection = ({ leadId, offer, onSaved, readOnly }) => {
  const proofs = (offer.documents || []).filter((d) => d.kind === 'OFFER_PROOF');
  const attach = async (doc) => {
    try {
      const res = await loanProfilingApi.attachOfferDocument(leadId, offer.id, { doc_id: doc.id, kind: 'OFFER_PROOF' });
      onSaved(res.data);
      toast.success('Attached to this offer');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not attach the file');
    }
  };
  return (
    <div data-testid={`offer-proofs-${offer.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-800">Bank's offer on record</h4>
          <p className="text-[11px] text-slate-400">The mail screenshot or sanction letter the bank sent.</p>
        </div>
        {!readOnly && (
          <OfferUpload leadId={leadId} docType="bank_offer" label="Upload proof" testId={`proof-upload-${offer.id}`} onUploaded={attach} />
        )}
      </div>
      {proofs.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid={`proof-empty-${offer.id}`}>Nothing attached yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {proofs.map((p) => (
            <li key={p.doc_id} className="flex items-center gap-2 text-xs text-slate-500">
              <Paperclip className="w-3.5 h-3.5 text-slate-300" />
              <DocLink leadId={leadId} docId={p.doc_id} name={p.file_name} testId={`proof-doc-${offer.id}-${p.doc_id}`} />
              <span className="text-slate-400 ml-auto">
                {new Date(p.attached_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} · {p.attached_by}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ReopenSection = ({ leadId, offer, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [channel, setChannel] = useState('WHATSAPP');
  const [note, setNote] = useState('');
  const move = async () => {
    setBusy(true);
    try {
      const res = await loanProfilingApi.recordCustomerDecision(leadId, offer.id, {
        channel, note: note || 'Customer changed their mind',
      });
      onSaved(res.data);
      setNote('');
      toast.success(`${offer.bank_name} is now the customer's choice`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not move the choice');
    } finally { setBusy(false); }
  };
  return (
    <div data-testid={`offer-reopen-${offer.id}`}>
      <p className="text-xs text-slate-500 mb-2">
        The customer went with another bank. If they change their mind, move their choice back to {offer.bank_name}.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="They told us over">
          <Picker value={channel} onChange={setChannel} options={CHANNELS} testId={`reopen-channel-${offer.id}`} />
        </Field>
        <Field label="Note" className="flex-1 min-w-[200px]">
          <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 h-9"
            placeholder="Customer changed their mind" data-testid={`reopen-note-${offer.id}`} />
        </Field>
        <Button size="sm" variant="outline" onClick={move} disabled={busy} data-testid={`offer-reopen-btn-${offer.id}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5 mr-1.5" />}
          Customer chose this instead
        </Button>
      </div>
    </div>
  );
};

// ── the bank list on the left ──────────────────────────────────────────────
export const OfferRow = ({ offer, comparison, selected, onSelect }) => {
  const readOnly = offer.status === 'NOT_SELECTED';
  const accepted = offer.status === 'ACCEPTED' || offer.status === 'DISBURSED';
  return (
    <button onClick={onSelect} data-testid={`offer-row-${offer.id}`}
      className={`w-full text-left rounded-xl border px-3.5 py-3 transition-colors
        ${selected ? 'border-blue-400 bg-blue-50/60 shadow-sm'
          : accepted ? 'border-emerald-200 bg-white hover:border-emerald-300'
            : readOnly ? 'border-slate-200 bg-slate-50 hover:bg-slate-100'
              : 'border-slate-200 bg-white hover:border-slate-300'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 leading-tight truncate">{offer.bank_name}</div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            <StatusPill status={offer.status} testId={`offer-status-${offer.id}`} />
            {comparison?.lowest_emi_offer_id === offer.id && <Tag tone="emerald" testId={`tag-lowest-emi-${offer.id}`}>Lowest EMI</Tag>}
            {comparison?.highest_payout_offer_id === offer.id && <Tag tone="amber" testId={`tag-best-payout-${offer.id}`}>Best payout</Tag>}
            {offer.meets_customer_ask && (
              offer.meets_customer_ask.met
                ? <Tag tone="emerald" testId={`tag-meets-ask-${offer.id}`}>Meets the ask</Tag>
                : <Tag tone="rose" testId={`tag-above-ask-${offer.id}`}>
                    {offer.meets_customer_ask.roi_ok === false
                      ? `${offer.meets_customer_ask.roi_gap}% above the ask`
                      : `${inr(offer.meets_customer_ask.emi_gap)} over the EMI asked`}
                  </Tag>
            )}
          </div>
        </div>
        <ChevronRight className={`w-4 h-4 mt-0.5 shrink-0 ${selected ? 'text-blue-500' : 'text-slate-300'}`} />
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2.5">
        <Metric label="Rate" value={offer.roi ? `${offer.roi}%` : '—'} testId={`offer-roi-${offer.id}`} accent />
        <Metric label="EMI" value={inr(offer.emi)} testId={`offer-emi-${offer.id}`} />
        <Metric label="Payout" value={offer.commission_pct ? `${offer.commission_pct}%` : '—'} testId={`offer-payout-${offer.id}`} />
      </div>
    </button>
  );
};

// ── the work on the right, one section at a time ───────────────────────────
const PANES = [
  { key: 'details', label: 'Details', icon: SlidersHorizontal },
  { key: 'negotiation', label: 'Negotiation', icon: MessageSquare },
  { key: 'documents', label: 'Documents', icon: FolderOpen },
];

export const OfferPanel = ({ leadId, offer, comparison, onSaved, fileDisbursed }) => {
  const [pane, setPane] = useState('details');
  const [removing, setRemoving] = useState(false);
  const readOnly = offer.status === 'NOT_SELECTED';
  const accepted = offer.status === 'ACCEPTED' || offer.status === 'DISBURSED';

  const remove = async () => {
    if (!window.confirm(`Remove the ${offer.bank_name} offer?`)) return;
    setRemoving(true);
    try {
      const res = await loanProfilingApi.deleteOffer(leadId, offer.id);
      onSaved(res.data);
      toast.success('Offer removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove this offer');
    } finally { setRemoving(false); }
  };

  return (
    <div className={`rounded-xl border bg-white ${accepted ? 'border-emerald-300' : 'border-slate-200'}`}
      data-testid={`offer-panel-${offer.id}`}>
      <div className="px-4 pt-4 pb-0 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900 leading-tight">{offer.bank_name}</h3>
          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-slate-500">
            <span className="tabular-nums">{offer.roi ? `${offer.roi}%` : '—'} · {offer.tenure_months ? `${offer.tenure_months} months` : '—'} · EMI {inr(offer.emi)}</span>
            <span>Net to the dealer <strong className="text-slate-700">{inr(offer.net_disbursement)}</strong></span>
            {offer.commission_pct ? <span>Our payout {offer.commission_pct}% · {inr(offer.commission_amount)}</span> : null}
          </div>
        </div>
        {!readOnly && !accepted && (
          <button onClick={remove} disabled={removing} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200"
            data-testid={`offer-remove-${offer.id}`} title={`Remove the ${offer.bank_name} offer`}>
            {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </button>
        )}
      </div>

      <div className="px-4 mt-3 flex items-center gap-1 border-b border-slate-200">
        {PANES.map((p) => {
          const Icon = p.icon;
          const on = pane === p.key;
          return (
            <button key={p.key} onClick={() => setPane(p.key)} data-testid={`offer-pane-${p.key}`}
              className={`relative inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors
                ${on ? 'text-blue-700' : 'text-slate-500 hover:text-slate-800'}`}>
              <Icon className="w-3.5 h-3.5" />{p.label}
              <span className={`absolute left-2 right-2 -bottom-px h-0.5 rounded-full ${on ? 'bg-blue-600' : 'bg-transparent'}`} />
            </button>
          );
        })}
      </div>

      <div className="p-4 space-y-6" data-testid={`offer-body-${offer.id}`}>
        {pane === 'details' && (
          <>
            <div className="pb-2">
              <TermsEditor leadId={leadId} offer={offer} onSaved={onSaved} readOnly={readOnly} />
            </div>
            <OfferCharges leadId={leadId} offer={offer} onSaved={onSaved} readOnly={readOnly} />
            <div className="pt-4 border-t border-slate-100">
              {readOnly
                ? (!fileDisbursed && <ReopenSection leadId={leadId} offer={offer} onSaved={onSaved} />)
                : (fileDisbursed && !accepted
                  ? <p className="text-xs text-slate-500" data-testid={`offer-locked-note-${offer.id}`}>
                      This file has already been disbursed, so the customer's choice cannot be moved to {offer.bank_name}.
                    </p>
                  : <DecisionSection leadId={leadId} offer={offer} onSaved={onSaved} />)}
            </div>
          </>
        )}
        {pane === 'negotiation' && (
          <OfferNegotiation leadId={leadId} offer={offer} onSaved={onSaved} readOnly={readOnly} />
        )}
        {pane === 'documents' && (
          <>
            <ProofSection leadId={leadId} offer={offer} onSaved={onSaved} readOnly={readOnly} />
            <div className="pt-4 border-t border-slate-100">
              <LetterSection leadId={leadId} offer={offer} onSaved={onSaved} readOnly={readOnly || offer.status === 'DISBURSED'} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
